"use client";

// French copy of the slim mode of components/widgetFeatures (FR-1), the
// only mode the enriched hub uses. Feature titles are French here, but the
// outline icons are keyed by the English database titles, so each feature
// carries its English title as `enTitle` and the icon lookup uses that.
import React from "react";
import Image from "next/image";
import { IPlugin } from "../../types";
import { StyledDiv } from "../widgetFeatures/widgetFeatures.styles";
import {
  IExtraBoxFeature,
  ISlimBoxIcons,
} from "../widgetFeatures/widgetFeatures.comp";

// Same outline style as the English component's SLIM_BOX_ICONS fallback.
const ICON_PROPS = {
  width: 28,
  height: 28,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

// The English component's global fallback icons, keyed by English title.
const FALLBACK_ICONS: Record<string, React.ReactNode> = {
  "Date & Time Display Settings": (
    <svg {...ICON_PROPS} aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="3" x2="8" y2="7" />
      <line x1="16" y1="3" x2="16" y2="7" />
      <polyline points="11 13 11 16 14 16" />
    </svg>
  ),
  "Customize Character Display Length": (
    <svg {...ICON_PROPS} aria-hidden="true">
      <polyline points="4 7 4 4 20 4 20 7" />
      <line x1="9" y1="20" x2="15" y2="20" />
      <line x1="12" y1="4" x2="12" y2="20" />
    </svg>
  ),
  "Full RTL Support": (
    <svg {...ICON_PROPS} aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="9" y1="12" x2="21" y2="12" />
      <line x1="6" y1="18" x2="21" y2="18" />
    </svg>
  ),
  "Easy To Use widget": (
    <svg {...ICON_PROPS} aria-hidden="true">
      <path d="M12 3l1.8 5.2 5.2 1.8-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M19 16l0.7 1.8 1.8 0.7-1.8 0.7L19 21l-0.7-1.8L16.5 18.5l1.8-0.7z" />
    </svg>
  ),
};

function stripHtml(html: string): string {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

export interface IFrExtraBoxFeature extends IExtraBoxFeature {
  enTitle?: string;
}

type FrFeature = IPlugin["features"][number] & { enTitle?: string };

export const WidgetFeaturesFr = ({
  widget,
  parentHeading,
  extraBoxFeatures,
  slimBoxIcons,
}: {
  widget: IPlugin;
  parentHeading?: string;
  extraBoxFeatures?: IFrExtraBoxFeature[];
  slimBoxIcons?: ISlimBoxIcons;
}) => {
  const features = widget.features as FrFeature[];
  const sections = features.filter((f) => f.type === "section" || !f.type);
  const boxes = [
    ...features.filter((f) => f.type === "box"),
    ...(extraBoxFeatures || []).map((b) => ({
      ...b,
      type: "box" as const,
      imgUrl: b.imgUrl || "",
    })),
  ];

  const iconFor = (f: { title: string; enTitle?: string; imgUrl?: string }) => {
    const key = f.enTitle || f.title;
    return (
      slimBoxIcons?.[key] ||
      FALLBACK_ICONS[key] ||
      (f.imgUrl && <Image src={f.imgUrl} alt="" width={30} height={30} />)
    );
  };

  return (
    <StyledDiv className="slim">
      {parentHeading && <h2 className="features-heading">{parentHeading}</h2>}

      <ul className="feature-grid">
        {sections.map((feature) => (
          <li key={feature.title} className="feature-card">
            <div className="img-wrap">
              <Image
                src={feature.imgUrl}
                alt={`${widget?.name}\u00a0: ${feature.title}`}
                width={685}
                height={428}
                sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
              />
            </div>
            <div className="card-body">
              <h3>{feature.title}</h3>
              <p>{stripHtml(feature.text)}</p>
            </div>
          </li>
        ))}
      </ul>

      {boxes.length > 0 && (
        <>
          <h2 className="extras-heading">{"Également inclus dans l'éditeur"}</h2>
          <div className="benefits">
            {boxes.map((feature) => (
              <div key={feature.title} className="card">
                <span className="icon-wrap" aria-hidden="true">
                  {iconFor(feature)}
                </span>
                <h3>{feature.title}</h3>
                <p>{stripHtml(feature.text)}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </StyledDiv>
  );
};
